import React, { useState, useEffect, useRef } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { useSearchCharityQuery } from "../api";

export interface CharityRecord {
    users_donate_id: number;
    users_donate_user_id: number;
    users_donate_walet: number;
    users_donate_usdt_walet: number;
    users_donate_status: number;
    user: {
        id: number;
        name: string;
        surname: string;
        email: string;
        mobile: string | null;
        secret_cookie: string | null;
    } | null;
}

interface CharityAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onCharitySelect?: (charity: CharityRecord) => void;
    placeholder?: string;
    className?: string;
}

export const CharityAutocomplete: React.FC<CharityAutocompleteProps> = ({
    value,
    onChange,
    onCharitySelect,
    placeholder = "ID благо, ID пользователя, email, телефон, имя...",
    className = "",
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [wasSelected, setWasSelected] = useState(false);
    const debouncedValue = useDebounce(value, 300);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Используем RTK Query для поиска
    const { data, isLoading, isFetching } = useSearchCharityQuery(
        { query: debouncedValue, limit: 10 },
        {
            skip: !debouncedValue || debouncedValue.length < 1,
        }
    );

    const suggestions = data?.data || [];

    // Сбрасываем флаг выбора при изменении значения пользователем
    useEffect(() => {
        // Если значение изменилось и это не просто ID (пользователь печатает)
        if (value && !/^\d+$/.test(value)) {
            setWasSelected(false);
        }
    }, [value]);

    // Показываем dropdown когда есть данные, но не после выбора
    useEffect(() => {
        if (debouncedValue && debouncedValue.length >= 1 && !wasSelected) {
            setShowDropdown(true);
        } else if (wasSelected || debouncedValue.length < 1) {
            setShowDropdown(false);
        }
    }, [debouncedValue, data, wasSelected]);

    // Закрытие при клике вне
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleCharityClick = (charity: CharityRecord) => {
        setWasSelected(true);
        setShowDropdown(false);
        onChange(charity.users_donate_id.toString());
        onCharitySelect?.(charity);
    };

    const formatCharityDisplay = (charity: CharityRecord): string => {
        const parts = [`Благо #${charity.users_donate_id}`];

        if (charity.user) {
            const userParts = [
                `Пользователь #${charity.user.id}`,
                charity.user.name,
                charity.user.surname,
            ].filter(Boolean);

            parts.push(userParts.join(" "));

            if (charity.user.email) {
                parts.push(`(${charity.user.email})`);
            } else if (charity.user.mobile) {
                parts.push(`(${charity.user.mobile})`);
            }
        }

        return parts.join(" - ");
    };

    const getStatusBadge = (status: number) => {
        switch (status) {
            case 0:
                return (
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        Активный
                    </span>
                );
            case 1:
                return (
                    <span className="px-2 py-1 text-xs rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                        Неактивный
                    </span>
                );
            default:
                return (
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Статус {status}
                    </span>
                );
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                type="text"
                value={value}
                onChange={(e) => {
                    setWasSelected(false);
                    onChange(e.target.value);
                }}
                onFocus={() => {
                    // Показываем dropdown при фокусе, если есть результаты
                    if (suggestions.length > 0 && debouncedValue.length >= 1) {
                        setShowDropdown(true);
                    }
                }}
                className={`w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${className}`}
                placeholder={placeholder}
                autoComplete="off"
            />

            {(isLoading || isFetching) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
            )}

            {showDropdown && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((charity) => (
                        <div
                            key={charity.users_donate_id}
                            onClick={() => handleCharityClick(charity)}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {formatCharityDisplay(charity)}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        USD: {charity.users_donate_walet} |
                                        USDT: {charity.users_donate_usdt_walet}
                                    </div>
                                    {charity.user?.secret_cookie && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            Cookie: {charity.user.secret_cookie}
                                        </div>
                                    )}
                                </div>
                                <div className="ml-3 flex flex-col items-end gap-1">
                                    {getStatusBadge(
                                        charity.users_donate_status
                                    )}
                                    <div className="text-xs text-blue-600 dark:text-blue-400">
                                        #{charity.users_donate_id}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showDropdown &&
                !isLoading &&
                !isFetching &&
                debouncedValue.length >= 1 &&
                suggestions.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                        Записи не найдены
                    </div>
                )}
        </div>
    );
};
